// eslint-disable-next-line no-restricted-imports -- SDK boundary owns initialization and capture.
import posthog, { type Properties } from 'posthog-js';

import { isDefined } from 'common';

import * as logger from '../../logger';
import { type ContentFreeAgenticChatEvent } from './AgenticChat/schema';
import { type ContentFreeFlowAiEvent } from './FlowAi/schema';
import { getContentFreeChatTelemetryProperties } from './contentFreeChatTelemetryProperties';
import { type EventAction, EventCategory } from './types';

type CaptureEventOptions =
  | ({ category: EventCategory.AgenticChat } & ContentFreeAgenticChatEvent)
  | ({ category: EventCategory.FlowAi } & ContentFreeFlowAiEvent)
  | {
      category: Exclude<EventCategory, EventCategory.AgenticChat | EventCategory.FlowAi>;
      action: EventAction;
      properties?: Properties;
    };

/** React and non-React callers share the staff provider's global SDK instance. */
export function captureEvent(options: CaptureEventOptions): void {
  const { category, action, properties } = options;
  if (category === EventCategory.AgenticChat || category === EventCategory.FlowAi) {
    const contentFreeProperties = getContentFreeChatTelemetryProperties({
      category,
      action,
      properties,
      propertySource: 'Application',
      onInvalidKeyPaths: (keyPaths) => {
        if (category === EventCategory.FlowAi) {
          logger.error(
            new Error(`Dropped invalid ${category}:${action} telemetry at ${keyPaths.join(', ')}`)
          );
        }
      },
    });
    if (isDefined(contentFreeProperties)) {
      posthog.capture(`${category}:${action}`, contentFreeProperties);
    }
    return;
  }
  posthog.capture(`${category}:${action}`, properties);
}

/** Legacy compatibility wrapper; prefer importing `captureEvent` directly. */
export function useCaptureEvents() {
  return { captureEvent };
}
